<template>
	<v-card class="ma-5" flat :loading="loading">
		<v-card-text class="pa-0">
			<div class="panel-content px-4 py-4">
				<!-- Current Organization Connectors -->
				<div class="mb-4">
					<div class="d-flex align-center mb-2">
						<span class="font-weight-bold">{{ $t("current-organization") }}</span>
						<v-chip x-small class="ml-2" color="grey lighten-2">
							{{ currentOrgConnectors.length }}
						</v-chip>
					</div>

					<v-alert
						v-if="!loading && currentOrgConnectors.length === 0"
						type="info"
						outlined
						dense
					>
						{{ $t("connector.no-current-connectors") }}
					</v-alert>

					<v-list v-else class="pa-0">
						<connector-row
							v-for="row in currentOrgConnectors"
							:key="row.id"
							:row="row"
							:expanded-sessions="expandedSessions[row.id]"
							:sessions-loading="sessionsLoading[row.organizationId]"
							:sessions="sessions[row.organizationId] || []"
							@toggle-sessions="toggleSessions(row)"
							@update="openUpdateDialog(row)"
							@toggle-enabled="confirmToggleEnabled(row)"
							@delete="confirmDelete(row)"
							@copy="copyToClipboard"
						/>
					</v-list>
				</div>

				<v-divider class="mb-4" />

				<!-- Other Organizations -->
				<div>
					<div
						class="d-flex align-center mb-2 other-section-header"
						@click="showOther = !showOther"
					>
						<v-icon small class="mr-2">
							{{ showOther ? "mdi-chevron-down" : "mdi-chevron-right" }}
						</v-icon>
						<span class="font-weight-bold">{{ $t("connector.other-organizations") }}</span>
						<v-chip x-small class="ml-2" color="grey lighten-2">
							{{ otherConnectors.length }}
						</v-chip>
						<v-spacer />
						<span v-if="showOther && otherConnectors.length > 0" class="text-caption grey--text mr-2">
							{{ filteredOtherConnectors.length }} / {{ otherConnectors.length }}
						</span>
					</div>

					<div v-if="showOther">
						<v-text-field
							v-model="otherSearch"
							:placeholder="$t('search') + '...'"
							prepend-inner-icon="mdi-magnify"
							outlined
							dense
							clearable
							hide-details
							class="mb-2"
						/>

						<v-virtual-scroll
							v-if="filteredOtherConnectors.length"
							:items="filteredOtherConnectors"
							:item-height="expandedRowHeight"
							:height="virtualHeight"
						>
							<template #default="{ item }">
								<connector-row
									:row="item"
									:expanded-sessions="expandedSessions[item.id]"
									:sessions-loading="sessionsLoading[item.organizationId]"
									:sessions="sessions[item.organizationId] || []"
									@toggle-sessions="toggleSessions(item)"
									@update="openUpdateDialog(item)"
									@toggle-enabled="confirmToggleEnabled(item)"
									@delete="confirmDelete(item)"
									@copy="copyToClipboard"
								/>
							</template>
						</v-virtual-scroll>

						<v-alert
							v-else-if="otherSearch && !loading"
							type="info"
							outlined
							dense
						>
							{{ $t("no-results") }}
						</v-alert>
					</div>
				</div>
			</div>
		</v-card-text>

		<!-- Update Connector Dialog -->
		<v-dialog v-model="updateDialog" max-width="500" persistent content-class="devops-subdialog">
			<v-card class="py-6 px-3">
				<v-card-title class="text-h6 font-weight-bold py-0 px-4">
					{{ $t("connector.update-title") }}
					<v-spacer />
					<v-btn icon small @click="updateDialog = false">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>
				<v-divider class="my-4 mx-4" />
				<v-card-text class="pb-2">
					<v-form ref="updateForm" v-model="updateFormValid">
						<v-text-field
							v-model="updateData.crmId"
							:label="$t('connector.crm-id')"
							:rules="[v => !!(v && v.trim()) || $t('error.validator.required')]"
							outlined
							dense
							@blur="updateData.crmId = (updateData.crmId || '').trim()"
						/>
					</v-form>
				</v-card-text>
				<v-card-actions class="px-4 pt-4 pb-2 d-flex justify-space-between">
					<v-btn color="error" @click="updateDialog = false">
						{{ $t("cancel") }}
					</v-btn>
					<v-btn
						color="primary"
						:disabled="!updateFormValid || saving"
						:loading="saving"
						@click="saveUpdate"
					>
						{{ $t("save") }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<confirmation-dialog ref="confirmDialog" />
	</v-card>
</template>

<script>
import ConfirmationDialog from "@/components/configuration/ConfirmationDialog.vue";
import ConnectorRow from "@/components/support/ConnectorRow.vue";
import connectorDevopsService from "@/services/connectorDevopsService";

export default {
	name: "ConnectorPanel",

	components: {
		ConfirmationDialog,
		ConnectorRow
	},

	data() {
		return {
			loading: false,
			rows: [],
			showOther: false,
			otherSearch: "",
			expandedSessions: {},
			sessions: {},
			sessionsLoading: {},
			updateDialog: false,
			updateFormValid: false,
			updateData: { id: null, type: "", organizationId: "", crmId: "" },
			saving: false,
			expandedRowHeight: 220,
			virtualHeight: 480
		};
	},

	computed: {
		currentOrgId() {
			return this.$store.state.clinic?.clinicData?.id || this.$store.state.clinic?.clinicId;
		},

		currentOrgConnectors() {
			return this.rows.filter(r => r.organizationId === this.currentOrgId);
		},

		otherConnectors() {
			return this.rows.filter(r => r.organizationId !== this.currentOrgId);
		},

		filteredOtherConnectors() {
			if (!this.otherSearch) return this.otherConnectors;
			const q = this.normalizeText(this.otherSearch);

			return this.otherConnectors.filter(r => {
				return (
					this.normalizeText(r.organizationName || "").includes(q) ||
					this.normalizeText(r.organizationId || "").includes(q) ||
					this.normalizeText(r.type || "").includes(q) ||
					this.normalizeText(r.crmId || "").includes(q)
				);
			});
		}
	},

	mounted() {
		this.fetchAll();
	},

	methods: {
		normalizeText(text) {
			return String(text || "")
				.toLowerCase()
				.normalize("NFD")
				.replace(/[\u0300-\u036f]/g, "");
		},

		async fetchAll({ silent = false } = {}) {
			if (!silent) this.loading = true;
			try {
				this.rows = await connectorDevopsService.listOrganizationConnectors();
			} catch (err) {
				console.error("Failed to load connectors:", err);
				if (!silent) {
					this.$error(this.$t("connector.error.list-failed"));
				}
			} finally {
				this.loading = false;
			}
		},

		async copyToClipboard(text) {
			try {
				await navigator.clipboard.writeText(text);
				this.$success(this.$t("copied-to-clipboard"));
			} catch (err) {
				console.error("Failed to copy:", err);
				this.$error(this.$t("error.copy-failed"));
			}
		},

		async toggleSessions(row) {
			const wasExpanded = !!this.expandedSessions[row.id];

			this.$set(this.expandedSessions, row.id, !wasExpanded);

			if (wasExpanded) return;
			if (this.sessions[row.organizationId]) return;

			this.$set(this.sessionsLoading, row.organizationId, true);
			try {
				const sessions = await connectorDevopsService.listSessions(
					row.organizationId
				);

				this.$set(this.sessions, row.organizationId, sessions);
			} catch (err) {
				console.error("Failed to load sessions:", err);
			} finally {
				this.$set(this.sessionsLoading, row.organizationId, false);
			}
		},

		openUpdateDialog(row) {
			this.updateData = {
				id: row.id,
				type: row.type,
				organizationId: row.organizationId,
				crmId: row.crmId
			};
			this.updateDialog = true;
		},

		async saveUpdate() {
			if (!this.$refs.updateForm.validate()) return;
			this.saving = true;
			try {
				await connectorDevopsService.updateOrganizationConnector(
					this.updateData.type,
					this.updateData.organizationId,
					{ crmId: this.updateData.crmId.trim() }
				);

				this.$success(this.$t("connector.update-success"));
				this.updateDialog = false;
				await this.fetchAll({ silent: true });
			} catch (err) {
				console.error("Failed to update connector:", err);
				this.$error(
					err.response?.data?.message || this.$t("connector.error.update-failed")
				);
			} finally {
				this.saving = false;
			}
		},

		async confirmToggleEnabled(row) {
			const enabling = !row.enabled;

			try {
				await this.$refs.confirmDialog.open({
					title: enabling
						? this.$t("connector.enable-title")
						: this.$t("connector.disable-title"),
					body: enabling
						? this.$t("connector.enable-confirm")
						: this.$t("connector.disable-confirm"),
					confirm: this.$t("confirm")
				});

				await this.toggleEnabled(row, enabling);
			} catch {
				// cancelled
			}
		},

		async toggleEnabled(row, enabling) {
			try {
				await connectorDevopsService.updateOrganizationConnector(
					row.type,
					row.organizationId,
					{ enabled: enabling }
				);

				this.$success(
					enabling
						? this.$t("connector.enable-success")
						: this.$t("connector.disable-success")
				);
				await this.fetchAll({ silent: true });
			} catch (err) {
				console.error("Failed to toggle connector:", err);
				this.$error(
					err.response?.data?.message || this.$t("connector.error.update-failed")
				);
			}
		},

		async confirmDelete(row) {
			try {
				await this.$refs.confirmDialog.open({
					title: this.$t("connector.delete-title"),
					body: this.$t("connector.delete-warning", {
						organization: row.organizationName || row.organizationId,
						type: row.connectorLabel || row.type
					}),
					confirm: this.$t("delete")
				});

				await this.deleteConnector(row);
			} catch {
				// cancelled
			}
		},

		async deleteConnector(row) {
			try {
				await connectorDevopsService.deleteOrganizationConnector(
					row.type,
					row.organizationId
				);

				this.$success(this.$t("connector.delete-success"));
				// invalidate sessions cache for that org
				this.$delete(this.sessions, row.organizationId);
				await this.fetchAll({ silent: true });
			} catch (err) {
				console.error("Failed to delete connector:", err);
				this.$error(
					err.response?.data?.message || this.$t("connector.error.delete-failed")
				);
			}
		}
	}
};
</script>

<style scoped>
.connector-item {
	border: 1px solid #e0e0e0;
	border-radius: 4px;
	padding: 8px;
}

.other-section-header {
	cursor: pointer;
	user-select: none;
}

.copy-icon {
	cursor: pointer;
	opacity: 0.6;
}

.copy-icon:hover {
	opacity: 1;
}

.session-item {
	padding: 4px 0;
	border-top: 1px dashed #eee;
}
</style>
